// src/pages/Activities.tsx
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PageHero } from '@/components/PageHero';
import {
  activities,
  activityStats,
  getFeaturedActivities,
  type Activity,
  type ActivityType,
  type ActivityCategory,
  type ActivityStatus
} from '@/data/mock/activities';
import {
  Calendar,
  MapPin,
  Users,
  Globe,
  Plane,
  Trophy,
  Target,
  Clock,
  Search,
  Filter,
  X,
  Star,
  Tent,
  Flag,
  Award,
  ChevronRight,
  ExternalLink,
  PlayCircle,
  Image as ImageIcon,
  CheckCircle2,
  TrendingUp,
  Sparkles,
  ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';

const Activities = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // State
  const [selectedType, setSelectedType] = useState<ActivityType | 'all'>('all');
  const [selectedCategory, setSelectedCategory] = useState<ActivityCategory | 'all'>('all');
  const [selectedStatus, setSelectedStatus] = useState<ActivityStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Type filters
  const typeFilters = [
    { 
      value: 'all' as const, 
      label: { en: 'All Activities', ar: 'جميع الأنشطة' },
      icon: Sparkles,
      color: 'from-blue-500 to-cyan-500'
    },
    { 
      value: 'camp' as const, 
      label: { en: 'Camps', ar: 'المعسكرات' },
      icon: Tent,
      color: 'from-green-500 to-emerald-500'
    },
    { 
      value: 'local_trip' as const, 
      label: { en: 'Local Trips', ar: 'رحلات محلية' },
      icon: Flag,
      color: 'from-purple-500 to-pink-500'
    },
    { 
      value: 'international_trip' as const, 
      label: { en: 'International', ar: 'رحلات دولية' },
      icon: Plane,
      color: 'from-orange-500 to-red-500'
    },
    { 
      value: 'event' as const, 
      label: { en: 'Events', ar: 'فعاليات' },
      icon: Trophy,
      color: 'from-yellow-500 to-amber-500'
    },
    { 
      value: 'tournament' as const, 
      label: { en: 'Tournaments', ar: 'بطولات' },
      icon: Award,
      color: 'from-indigo-500 to-purple-500'
    },
  ];

  // Status filters
  const statusFilters = [
    { value: 'all' as const, label: { en: 'All', ar: 'الكل' } },
    { value: 'upcoming' as const, label: { en: 'Upcoming', ar: 'قادمة' } },
    { value: 'ongoing' as const, label: { en: 'Ongoing', ar: 'جارية' } },
    { value: 'completed' as const, label: { en: 'Completed', ar: 'منتهية' } },
  ];

  // Filter activities
  const filteredActivities = useMemo(() => {
    let result = activities;

    // Type filter
    if (selectedType !== 'all') {
      result = result.filter(a => a.type === selectedType);
    }

    // Category filter
    if (selectedCategory !== 'all') {
      result = result.filter(a => a.category === selectedCategory);
    }

    // Status filter
    if (selectedStatus !== 'all') {
      result = result.filter(a => a.status === selectedStatus);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(a =>
        a.title.en.toLowerCase().includes(query) ||
        a.title.ar.includes(searchQuery) ||
        a.description.en.toLowerCase().includes(query) ||
        a.description.ar.includes(searchQuery) ||
        a.location.en.toLowerCase().includes(query) ||
        a.location.ar.includes(searchQuery)
      );
    }

    // Featured filter
    if (showFeaturedOnly) {
      result = result.filter(a => a.featured === true);
    }

    // Sort by date (newest first)
    result.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    return result;
  }, [selectedType, selectedCategory, selectedStatus, searchQuery, showFeaturedOnly]);

  // Statistics
  const stats = [
    {
      icon: Sparkles,
      value: activityStats.total,
      label: { en: 'Total Activities', ar: 'إجمالي الأنشطة' },
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Users,
      value: `${(activityStats.totalParticipants / 1000).toFixed(1)}K+`,
      label: { en: 'Participants', ar: 'مشاركون' },
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Globe,
      value: activityStats.countries,
      label: { en: 'Countries Visited', ar: 'دول تمت زيارتها' },
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: Trophy,
      value: activityStats.completed,
      label: { en: 'Completed', ar: 'منجزة' },
      color: 'from-purple-500 to-pink-500'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <PageHero
        title={isRTL ? 'الأنشطة والفعاليات' : 'Activities & Events'}
        subtitle={isRTL
          ? 'معسكرات تدريبية، رحلات محلية ودولية، بطولات وفعاليات مجتمعية تطور مهارات اللاعبين وتبني روح الفريق'
          : 'Training camps, local and international trips, tournaments and community events that develop player skills and build team spirit'
        }
        breadcrumbs={[
          { label: t('common.home'), path: '/' },
          { label: t('nav.activities') }
        ]}
      />

      {/* Statistics */}
      <section className="py-12 bg-gradient-to-b from-background to-surface-1">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="p-6 rounded-2xl bg-card border-2 border-border hover:border-primary/30 shadow-xl text-center transition-all"
              >
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} bg-opacity-10 mb-3`}>
                  <stat.icon className="h-6 w-6 text-primary" />
                </div>
                <div className="text-3xl font-bold gradient-text mb-1">{stat.value}</div>
                <div className="text-xs text-muted-foreground">
                  {stat.label[isRTL ? 'ar' : 'en']}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Activities */}
      {getFeaturedActivities().length > 0 && (
        <section className="py-20 bg-surface-1">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-yellow-500/10 border border-yellow-500/20 mb-8">
                <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                <span className="text-sm font-semibold text-yellow-500">
                  {isRTL ? 'أنشطة مميزة' : 'Featured Activities'}
                </span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold mb-4 gradient-text">
                {isRTL ? 'أبرز أنشطتنا' : 'Our Highlighted Activities'}
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                {isRTL
                  ? 'أهم المعسكرات والرحلات والفعاليات التي نظمناها'
                  : 'Top camps, trips, and events we have organized'
                }
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {getFeaturedActivities().slice(0, 6).map((activity, idx) => (
                <FeaturedActivityCard
                  key={activity.id}
                  activity={activity}
                  isRTL={isRTL}
                  delay={idx * 0.1}
                  onClick={() => setSelectedActivity(activity)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Filters & Search */}
      <section className="py-12 bg-gradient-to-b from-surface-1 to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Filter className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'تصفح الأنشطة' : 'Browse Activities'}
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'استكشف جميع الأنشطة' : 'Explore All Activities'}
            </h2>
          </motion.div>

          {/* Type Filters */}
          <div className="flex justify-center gap-3 mb-8 flex-wrap max-w-5xl mx-auto">
            {typeFilters.map((filter) => {
              const Icon = filter.icon;
              const isActive = selectedType === filter.value;
              
              return (
                <motion.button
                  key={filter.value}
                  onClick={() => setSelectedType(filter.value)}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                      : 'bg-card border-2 border-border hover:border-primary/50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="font-medium">{filter.label[isRTL ? 'ar' : 'en']}</span>
                  {isActive && (
                    <Badge variant="secondary" className="text-xs">
                      {filteredActivities.length}
                    </Badge>
                  )}
                </motion.button>
              );
            })}
          </div>

          {/* Search & Advanced Filters */}
          <div className="max-w-4xl mx-auto space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'right-4' : 'left-4'} h-5 w-5 text-muted-foreground`} />
              <Input
                type="text"
                placeholder={isRTL ? 'ابحث عن نشاط...' : 'Search for an activity...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`${isRTL ? 'pr-12 pl-12' : 'pl-12 pr-12'} h-14 text-lg`}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-4' : 'right-4'} text-muted-foreground hover:text-foreground`}
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* Advanced Filters Row */}
            <div className="flex gap-4 flex-wrap items-center justify-between">
              <div className="flex gap-4 flex-wrap">
                {/* Status Filter */}
                <div className="flex gap-2">
                  {statusFilters.map((status) => (
                    <Button
                      key={status.value}
                      variant={selectedStatus === status.value ? 'default' : 'outline'}
                      onClick={() => setSelectedStatus(status.value)}
                      size="sm"
                    >
                      {status.label[isRTL ? 'ar' : 'en']}
                    </Button>
                  ))}
                </div>

                {/* Featured Toggle */}
                <Button
                  variant={showFeaturedOnly ? 'default' : 'outline'}
                  onClick={() => setShowFeaturedOnly(!showFeaturedOnly)}
                  size="sm"
                  className="gap-2"
                >
                  <Star className={`h-4 w-4 ${showFeaturedOnly ? 'fill-current' : ''}`} />
                  {isRTL ? 'المميزة فقط' : 'Featured Only'}
                </Button>
              </div>

              {/* Results Count */}
              <div className="text-sm text-muted-foreground">
                {isRTL 
                  ? `عرض ${filteredActivities.length} من ${activities.length} نشاط`
                  : `Showing ${filteredActivities.length} of ${activities.length} activities`
                }
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Activities Grid/List */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <AnimatePresence mode="wait">
            {filteredActivities.length > 0 ? (
              <motion.div
                key={`${selectedType}-${selectedStatus}-${searchQuery}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {filteredActivities.map((activity, idx) => (
                  <ActivityCard
                    key={activity.id}
                    activity={activity}
                    isRTL={isRTL}
                    delay={idx * 0.05}
                    onClick={() => setSelectedActivity(activity)}
                  />
                ))}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-20"
              >
                <Search className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="text-2xl font-bold mb-2">
                  {isRTL ? 'لم يتم العثور على نتائج' : 'No Results Found'}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {isRTL 
                    ? 'جرّب تعديل الفلاتر أو البحث'
                    : 'Try adjusting your filters or search'
                  }
                </p>
                <Button onClick={() => {
                  setSelectedType('all');
                  setSelectedStatus('all');
                  setSearchQuery('');
                  setShowFeaturedOnly(false);
                }}>
                  {isRTL ? 'إعادة تعيين الفلاتر' : 'Reset Filters'}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-8">
                <Calendar className="h-10 w-10 text-primary" />
              </div>
              
              <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
                {isRTL ? 'انضم إلى أنشطتنا القادمة' : 'Join Our Upcoming Activities'}
              </h2>
              
              <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
                {isRTL
                  ? 'كن جزءاً من تجارب رياضية فريدة ومعسكرات تدريبية عالمية ورحلات لا تُنسى'
                  : 'Be part of unique sports experiences, world-class training camps, and unforgettable trips'
                }
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button size="lg" className="btn-glow gap-2">
                    {isRTL ? 'سجل الآن' : 'Register Now'}
                    <ArrowRight className={`h-5 w-5 ${isRTL ? 'rotate-180' : ''}`} />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="gap-2">
                    {isRTL ? 'للاستفسار' : 'Contact Us'}
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Activity Detail Modal */}
      <ActivityDetailModal
        activity={selectedActivity}
        isRTL={isRTL}
        onClose={() => setSelectedActivity(null)}
      />
    </div>
  );
};

// ===== ACTIVITY CARD COMPONENT =====
const ActivityCard = ({ 
  activity, 
  isRTL, 
  delay = 0,
  onClick
}: { 
  activity: Activity; 
  isRTL: boolean;
  delay?: number;
  onClick: () => void;
}) => {
  const getTypeIcon = (type: ActivityType) => {
    const icons = {
      camp: Tent,
      local_trip: Flag,
      international_trip: Plane,
      event: Trophy,
      tournament: Award,
      workshop: Target
    };
    return icons[type] || Sparkles;
  };

  const getTypeColor = (type: ActivityType) => {
    const colors = {
      camp: 'from-green-500 to-emerald-500',
      local_trip: 'from-purple-500 to-pink-500',
      international_trip: 'from-orange-500 to-red-500',
      event: 'from-yellow-500 to-amber-500',
      tournament: 'from-indigo-500 to-purple-500',
      workshop: 'from-blue-500 to-cyan-500'
    };
    return colors[type] || 'from-gray-500 to-gray-600';
  };

  const Icon = getTypeIcon(activity.type);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      whileHover={{ y: -8, scale: 1.02 }}
      onClick={onClick}
      className="bg-card border-2 border-border rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl hover:border-primary/50 transition-all cursor-pointer group"
    >
      {/* Image */}
      <div className="aspect-video bg-muted relative overflow-hidden">
        <img
          src={activity.image}
          alt={activity.title[isRTL ? 'ar' : 'en']}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          loading="lazy"
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        
        {/* Badges */}
        <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
          {/* Type Badge */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r ${getTypeColor(activity.type)} text-white text-xs font-bold shadow-lg`}>
            <Icon className="h-3 w-3" />
            {activity.type.replace('_', ' ').toUpperCase()}
          </div>
          
          {/* Featured Badge */}
          {activity.featured && (
            <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-yellow-500 text-white text-xs font-bold shadow-lg">
              <Star className="h-3 w-3 fill-current" />
              {isRTL ? 'مميز' : 'FEATURED'}
            </div>
          )}
        </div>

        {/* Date */}
        <div className="absolute bottom-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-sm text-white text-xs">
          <Calendar className="h-3 w-3" />
          {new Date(activity.date).toLocaleDateString(isRTL ? 'ar' : 'en', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors line-clamp-2">
          {activity.title[isRTL ? 'ar' : 'en']}
        </h3>
        
        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
          {activity.description[isRTL ? 'ar' : 'en']}
        </p>

        {/* Meta Info */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3 text-primary" />
            <span className="line-clamp-1">{activity.location[isRTL ? 'ar' : 'en']}</span>
          </div>
          
          {activity.participants && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Users className="h-3 w-3 text-primary" />
              <span>{activity.participants} {isRTL ? 'مشارك' : 'participants'}</span>
            </div>
          )}

          {activity.duration && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="h-3 w-3 text-primary" />
              <span>{activity.duration} {isRTL ? 'يوم' : 'days'}</span>
            </div>
          )}
        </div>

        {/* CTA */}
        <Button
          variant="outline"
          size="sm"
          className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
        >
          {isRTL ? 'عرض التفاصيل' : 'View Details'}
          <ChevronRight className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''} ml-2`} />
        </Button>
      </div>
    </motion.div>
  );
};

// ===== FEATURED ACTIVITY CARD =====
const FeaturedActivityCard = ({ 
  activity, 
  isRTL, 
  delay = 0,
  onClick
}: { 
  activity: Activity; 
  isRTL: boolean;
  delay?: number;
  onClick: () => void;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay }}
      whileHover={{ y: -10, scale: 1.02 }}
      onClick={onClick}
      className="bg-card border-2 border-primary/30 rounded-3xl overflow-hidden shadow-2xl hover:shadow-primary/20 transition-all cursor-pointer group"
    >
      {/* Image */}
      <div className="aspect-video bg-gradient-to-br from-surface-1 to-muted relative overflow-hidden">
        <img
          src={activity.image}
          alt={activity.title[isRTL ? 'ar' : 'en']}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Featured Badge */}
        <div className="absolute top-4 right-4">
          <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-500 text-white text-xs font-bold">
            <Star className="h-3 w-3 fill-current" />
            {isRTL ? 'مميز' : 'FEATURED'}
          </div>
        </div>

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <h3 className="text-2xl font-bold text-white mb-2">
            {activity.title[isRTL ? 'ar' : 'en']}
          </h3>
          <div className="flex items-center gap-4 text-white/90 text-sm">
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {new Date(activity.date).getFullYear()}
            </div>
            <div className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              {activity.location[isRTL ? 'ar' : 'en']}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
          {activity.description[isRTL ? 'ar' : 'en']}
        </p>

        {/* Highlights */}
        {activity.highlights && (
          <div className="space-y-2 mb-4">
            {activity.highlights[isRTL ? 'ar' : 'en'].slice(0, 3).map((highlight, idx) => (
              <div key={idx} className="flex items-start gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="h-3 w-3 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="line-clamp-1">{highlight}</span>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <Button
          variant="default"
          size="sm"
          className="w-full"
        >
          {isRTL ? 'اقرأ المزيد' : 'Read More'}
          <ArrowRight className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''} ml-2`} />
        </Button>
      </div>
    </motion.div>
  );
};

// ===== ACTIVITY DETAIL MODAL =====
const ActivityDetailModal = ({
  activity,
  isRTL,
  onClose
}: {
  activity: Activity | null;
  isRTL: boolean;
  onClose: () => void;
}) => {
  if (!activity) return null;

  return (
    <Dialog open={!!activity} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold gradient-text">
            {activity.title[isRTL ? 'ar' : 'en']}
          </DialogTitle>
          <DialogDescription>
            <div className="flex items-center gap-4 flex-wrap mt-2">
              <Badge variant="secondary">{activity.type.replace('_', ' ')}</Badge>
              <Badge variant="outline">{activity.category}</Badge>
              {activity.status && <Badge>{activity.status}</Badge>}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Main Image */}
          <div className="aspect-video rounded-2xl overflow-hidden">
            <img
              src={activity.image}
              alt={activity.title[isRTL ? 'ar' : 'en']}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Quick Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-xl bg-surface-1 border border-border">
              <Calendar className="h-5 w-5 text-primary mb-2" />
              <div className="text-sm font-medium">
                {new Date(activity.date).toLocaleDateString(isRTL ? 'ar' : 'en', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
              <div className="text-xs text-muted-foreground">
                {isRTL ? 'التاريخ' : 'Date'}
              </div>
            </div>

            <div className="p-4 rounded-xl bg-surface-1 border border-border">
              <MapPin className="h-5 w-5 text-primary mb-2" />
              <div className="text-sm font-medium line-clamp-2">
                {activity.location[isRTL ? 'ar' : 'en']}
              </div>
              <div className="text-xs text-muted-foreground">
                {isRTL ? 'الموقع' : 'Location'}
              </div>
            </div>

            {activity.participants && (
              <div className="p-4 rounded-xl bg-surface-1 border border-border">
                <Users className="h-5 w-5 text-primary mb-2" />
                <div className="text-sm font-medium">{activity.participants}</div>
                <div className="text-xs text-muted-foreground">
                  {isRTL ? 'مشارك' : 'Participants'}
                </div>
              </div>
            )}

            {activity.duration && (
              <div className="p-4 rounded-xl bg-surface-1 border border-border">
                <Clock className="h-5 w-5 text-primary mb-2" />
                <div className="text-sm font-medium">
                  {activity.duration} {isRTL ? 'يوم' : 'days'}
                </div>
                <div className="text-xs text-muted-foreground">
                  {isRTL ? 'المدة' : 'Duration'}
                </div>
              </div>
            )}
          </div>

          {/* Full Description */}
          {activity.fullDescription && (
            <div>
              <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                {isRTL ? 'حول النشاط' : 'About the Activity'}
              </h4>
              <p className="text-muted-foreground leading-relaxed">
                {activity.fullDescription[isRTL ? 'ar' : 'en']}
              </p>
            </div>
          )}

          {/* Highlights */}
          {activity.highlights && (
            <div>
              <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                {isRTL ? 'أبرز الأحداث' : 'Highlights'}
              </h4>
              <div className="grid md:grid-cols-2 gap-3">
                {activity.highlights[isRTL ? 'ar' : 'en'].map((highlight, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 p-3 rounded-lg bg-surface-1"
                  >
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{highlight}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Achievements */}
          {activity.achievements && (
            <div>
              <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <Trophy className="h-5 w-5 text-primary" />
                {isRTL ? 'الإنجازات' : 'Achievements'}
              </h4>
              <div className="space-y-3">
                {activity.achievements[isRTL ? 'ar' : 'en'].map((achievement, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 p-4 rounded-xl bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20"
                  >
                    <Award className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm font-medium">{achievement}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Gallery */}
          {activity.gallery && activity.gallery.length > 0 && (
            <div>
              <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <ImageIcon className="h-5 w-5 text-primary" />
                {isRTL ? 'معرض الصور' : 'Photo Gallery'}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {activity.gallery.map((img, idx) => (
                  <div key={idx} className="aspect-square rounded-lg overflow-hidden">
                    <img
                      src={img}
                      alt={`${activity.title[isRTL ? 'ar' : 'en']} - ${idx + 1}`}
                      className="w-full h-full object-cover hover:scale-110 transition-transform"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Video */}
          {activity.videoUrl && (
            <div>
              <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <PlayCircle className="h-5 w-5 text-primary" />
                {isRTL ? 'فيديو النشاط' : 'Activity Video'}
              </h4>
              <a
                href={activity.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary hover:underline"
              >
                {isRTL ? 'شاهد الفيديو' : 'Watch Video'}
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Activities;
